// Bucket Contract v1 §6 — blurb template registry. N8N emits an
// `id + params` pair; the dashboard renders the prose. Adding a template id
// here is the only coordination needed (the backend doesn't validate ids).

type Params = Record<string, unknown>;

const TEMPLATES: Record<string, (p: Params) => string> = {
  GRN_MULTI_RECOMMENDED: ({ grn_nos, grn_total, subtotal }) =>
    `GRNs ${listJoin(grn_nos)} total Rs ${grn_total} against invoice subtotal Rs ${subtotal}.`,
  GRN_NONE: ({ supplier }) => `No GRNs found for supplier ${supplier}.`,
  GRN_AMOUNT_MISMATCH: ({ grn_no, grn_total, subtotal, delta }) =>
    `GRN ${grn_no} totals Rs ${grn_total} vs invoice subtotal Rs ${subtotal} (delta ${delta}).`,
  GRN_DETAIL_SPLIT_UNSUPPORTED: ({ grn_no, net_residual }) =>
    `GRN ${grn_no} includes a partial detail split; residual net Rs ${net_residual} requires manual review.`,
  TAX_MISMATCH_REVIEW: ({ grn_no, tax_rate }) =>
    `GRN ${grn_no} tax/STAX context needs review before retrying; invoice tax rate is ${tax_rate}%.`,
  DATE_REVIEW: ({ raw_date_text, review_reason }) =>
    `Invoice date '${raw_date_text ?? ''}' needs review: ${review_reason ?? 'date evidence is ambiguous'}.`,
  GRN_ALREADY_INVOICED_DUPLICATE_RISK: ({
    grn_nos,
    grn_detail_ids,
    purchase_invoice_id,
    ref_no,
    invoice_ref_matches,
    invoice_net_matches_grn_set,
    invoice_gross_matches_grn_set,
  }) =>
    `Exact GRN ${listJoin(grn_nos)} (${listJoin(grn_detail_ids)}) already invoiced under PI ${purchase_invoice_id} / ref ${ref_no}.${matchNote(
      invoice_ref_matches,
      invoice_net_matches_grn_set,
      invoice_gross_matches_grn_set,
    )}`,
  EXP_REB_REVIEW: () => 'Exp/Reb lines require Finance coding before posting.',
  SUPPLIER_AMBIGUOUS: ({ vendor, count }) =>
    `Supplier '${vendor}' matched ${count} candidates.`,
  SUPPLIER_NOT_FOUND: ({ ntn, vendor }) =>
    `No supplier found for NTN ${ntn} / vendor '${vendor}'.`,
  DUPLICATE_INVOICE_REF: ({ ref_no, posted_date }) =>
    `Duplicate of PI #${ref_no}${posted_date ? ` (posted ${posted_date})` : ''}.`,
  DUPLICATE_NO_AMOUNT: ({ invoice_no, amount }) =>
    `Invoice no ${invoice_no} with amount Rs ${amount} already exists for this supplier.`,
  DUPLICATE_GRN_SET: ({ grn_nos, ref_no }) =>
    `GRN set ${listJoin(grn_nos)} already invoiced under PI #${ref_no}.`,
  POST_FAILED: ({ failed_node, error_message }) =>
    `Oracle commit failed at ${failed_node}: ${error_message}.`,
  PARSE_ERROR: ({ failed_node, error_message }) =>
    `Invoice OCR parse failed at ${failed_node}: ${error_message}.`,
  SERVICE_PREP_READY: () =>
    `Service invoice ready for review. Pre-fill values populated from OCR.`,
  POSTED_OK: ({ ref_no, posted_date }) =>
    `Posted to Oracle as PI #${ref_no} on ${posted_date}.`,
  REJECTED_OK: ({ rejection_reason }) => `Rejected: ${rejection_reason}.`,
};

function listJoin(value: unknown): string {
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  if (value === null || value === undefined) return '';
  return String(value);
}

function matchNote(ref: unknown, net: unknown, gross: unknown): string {
  if (ref === true && net === true && gross === false) {
    return ' Invoice ref and net match this scan, but tax/gross differ.';
  }
  return '';
}

export function renderBlurb(id: string, params: Params = {}): string {
  const fn = TEMPLATES[id];
  if (!fn) return '—';
  try {
    return fn(params);
  } catch {
    return '—';
  }
}
